"use client"

import axios from "axios"
import { useEffect, useRef, useState } from "react"
import { FaEdit, FaSave } from "react-icons/fa"
import { FaExclamation } from "react-icons/fa6"
import { ImWarning } from "react-icons/im"
import { IoMdInformationCircleOutline } from "react-icons/io"
import { useLocation, useNavigate } from "react-router"

const feedbackQuestions = [
  {
    id: 1,
    question: "How would you rate the overall quality of this workshop?",
    type: "multiple-choice",
    required: true,
    options: ["Excellent", "Very Good", "Good", "Fair", "Poor"],
  },
  {
    id: 2,
    question: "Was the workshop content relevant to your needs?",
    type: "true-false",
    required: true,
    options: ["Yes", "No"],
  },
  {
    id: 3,
    question: "How likely are you to recommend this workshop to others?",
    type: "range",
    required: true,
    options: { min: 0, max: 10, minLabel: "Not likely", maxLabel: "Very likely" },
  },
  {
    id: 4,
    question: "Which of the following aspects of the event contributed most to your learning or overall experience?",
    type: "checkbox",
    required: true,
    options: [
      "Introduction & Overview",
      "Sessions",
      "Panel Discussion",
      "Q&A Session",
      "Networking Opportunities",
    ],
  },
  {
    id: 5,
    question: "How would you rate the instructor's presentation skills?",
    type: "multiple-choice",
    required: true,
    options: ["Excellent", "Very Good", "Good", "Fair", "Poor"],
  },
  {
    id: 6,
    question: "Do you feel Scientific Scholar is addressing the needs of editors and authors effectively?",
    type: "true-false",
    required: true,
    options: ["Yes", "No"],
  },
  {
    id: 7,
    question: "What topic would you like to hear in the future?",
    type: "open-ended",
    required: false,
    options: [],
  },
  {
    id: 8,
    question: "Should this workshop be continued on yearly basis?",
    type: "true-false",
    required: true,
    options: ["Yes", "No"],
  },
]

const FeedbackForm = () => {
  const [formData, setFormData] = useState({})
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const location = useLocation();
  const [user, setUser] = useState(localStorage.getItem("userId") || null);
  const [userName, setUserName] = useState("")
  const [userEmail, setUserEmail] = useState("")
  const inputNameRef = useRef(null);
  const [isNameDisabled, setIsNameDisabled] = useState(true)
  const navigate = useNavigate()
  useEffect(() => {
    const data = JSON.parse(localStorage.getItem("formData")) || {}
    setFormData(data);
    const getUserDetails  = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}api/getUser`, {
          params : {id:user}
        })
        setUserName(response.data?.name)
        setUserEmail(response.data?.email);
      } catch (err) {
        console.error(err);
      }
    }
    user ? getUserDetails() : navigate('/', { replace: true });
  }, [])
 useEffect(() => {
    if (Object.keys(formData).length > 0) {
        localStorage.setItem("formData", JSON.stringify(formData));
  }
 }, [formData])

  const handleInputChange = (questionId, value, type) => {
    if (type === "checkbox") {
      const currentValues = formData[questionId] || []
      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value]
      setFormData((prev) => ({ ...prev, [questionId]: newValues }))
    } else {
      setFormData((prev) => ({ ...prev, [questionId]: value }))
    }

    if (errors[questionId]) {
      setErrors((prev) => ({ ...prev, [questionId]: "" }))
    }

    
  }

  const validateForm = () => {
    const newErrors = {}
    feedbackQuestions.forEach((question) => {
      if (
        question.required &&
        (!formData[question.id] || (Array.isArray(formData[question.id]) && formData[question.id].length === 0))
      ) {
        newErrors[question.id] = "This field is required"
      }
    })
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) {
      return
    }
    setIsLoading(true)

    try {
        const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}api/submit-feedback`,  {
          userDetails : {
            id:user,
            name:userName,
            email:userEmail,
          }, feedback : {

            ...formData
          }
        })
        if (response.data.success) {
            setIsLoading(false)
            localStorage.removeItem("formData");
            localStorage.setItem("hasSubmitted", true)
            navigate("/thankyou", { replace: true })
        }
    } catch (err) {
        console.error(err);
    }
    
}

  const renderQuestion = (question) => {
    switch (question.type) {
      case "multiple-choice":
        return (
          <div className="radio-group">
            {question.options.map((option, index) => (
              <div key={index} className="radio-option">
                <input
                  type="radio"
                  checked = {formData[question.id]?.split(' ')[0]?.includes(option.split(' ')[0]) || false}
                  id={`q${question.id}_${index}`}
                  name={`question_${question.id}`}
                  value={option}
                  onChange={(e) => handleInputChange(question.id, e.target.value, "radio")}
                />
                <label htmlFor={`q${question.id}_${index}`}>{option}</label>
              </div>
            ))}
          </div>
        )

      case "true-false":
        return (
          <div className="radio-group">
            {question.options.map((option, index) => (
              <div key={index} className="radio-option">
                <input
                  type="radio"
                  checked = {formData[question.id]?.includes(option)}
                  id={`q${question.id}_${index}`}
                  name={`question_${question.id}`}
                  value={option}
                  onChange={(e) => handleInputChange(question.id, e.target.value, "radio")}
                />
                <label htmlFor={`q${question.id}_${index}`}>{option}</label>
              </div>
            ))}
          </div>
        )

      case "checkbox":
        return (
          <div className="checkbox-group">
            {question.options.map((option, index) => (
              <div key={index} className="checkbox-option">
                <input
                  type="checkbox"
                  checked = {formData[question.id]?.includes(option)}
                  id={`q${question.id}_${index}`}
                  value={option}
                  onChange={(e) => handleInputChange(question.id, e.target.value, "checkbox")}
                />
                <label htmlFor={`q${question.id}_${index}`}>{option}</label>
              </div>
            ))}
          </div>
        )

      case "range":
        return (
          <div className="range-container" style={{ position: "relative" }}>
            <input
              type="range"
              className="range-slider"
              min={question.options.min}
              max={question.options.max}
              value={formData[question.id] || question.options.min}
              onChange={(e) => handleInputChange(question.id, e.target.value, "range")}
              style={{ position: "relative", zIndex: 1 }}
            />

            {/* Line below slider */}
            <hr
              style={{
                position: "absolute",
                top: "40%", // push line below slider
                left: 0,
                width: "100%",
                zIndex: 0,
              }}
            />

            <div className="range-labels">
              <span>{question.options.minLabel}</span>
              <span>Current: {formData[question.id] || question.options.min}</span>
              <span>{question.options.maxLabel}</span>
            </div>
          </div>
        )

      case "open-ended":
        return (
          <textarea
          rows="3"
            className="textarea-input"
            placeholder="Please share your thoughts..."
            value={formData[question.id] || ""}
            onChange={(e) => handleInputChange(question.id, e.target.value, "textarea")}
          />
        )

      default:
        return null
    }
  }
  useEffect(() => {
    if (!isNameDisabled) {
      inputNameRef.current?.focus();
    }
  }, [isNameDisabled]);
  return (
    <div className="feedback-form">
      <h1 className="feedback-header">Workshop Feedback Form</h1>
      <p className="feedback-description">
        Your feedback is valuable to us. Please take a few minutes to share your experience.
      </p>

      <form onSubmit={handleSubmit}>
        <div className="question-container">
            <div className="question-title">
                Verify your name
            </div>
            
            <div style={{position:"relative", marginBottom:"-5px"}}>
                <textarea className="textarea-input"
                disabled = {isNameDisabled}
                placeholder="Your name"
                style={{backgroundColor:isNameDisabled ? "rgba(170, 170, 170, 1)" : "",
                    color:isNameDisabled ? "rgba(255, 255, 255, 1)" : "",
                    fontWeight:"400",
                    fontSize:"1rem"
                }}
                rows="1"
                name="name-input"
                ref={inputNameRef}
                value={userName}
                onChange={(e) => setUserName(e.target.value)} />
                <div style={{position:"absolute", right:"3%", top:"25%"}} 
                onClick={() => {
                    setIsNameDisabled(!isNameDisabled);
                }}>
                    {isNameDisabled ? 
                        <FaEdit style={{fontSize: "1.2rem" , cursor:"pointer",
                            color:"rgba(255, 255, 255, 1)" ,
                        }} />
                        :
                        <FaSave style={{fontSize: "1.2rem" , cursor:"pointer",
                            color: "blac",
                        }} />
                    }
                
                </div>
                
            </div>
            <span style={{fontSize:"0.8rem", alignItems:"center", display:"flex", gap:'4px'}}>
                <IoMdInformationCircleOutline style={{color:"rgba(168, 170, 0, 1)", fontSize:"1rem"}}/> This will be the name on your certificate.
            </span>
        </div>
        {feedbackQuestions.map((question) => (
          <div key={question.id} className="question-container">
            <div className="question-title">
              {question.question} 
              {question.required && <span className="question-required"> *</span>}
              <span style={{fontSize:"0.8rem"}}>{question.type === 'checkbox' && "(Please select all that apply)"} {question.type === 'range' && "(Slide to use)"}</span>
            </div>

            {renderQuestion(question)}

            {errors[question.id] && (
              <div
                style={{
                  color: "#ffffffff",
                  fontSize: "14px",
                  backgroundColor:"red",
                  marginTop: "8px",
                  display: "flex",
                  width:"40%",
                  alignItems: "center",
                  gap: "4px",
                  borderRadius:"10px",
                  padding:"5px"
                }}
              >
                <FaExclamation />
                {errors[question.id]}
              </div>
            )}
          </div>
        ))}

        <button type="submit" className="submit-btn" disabled={isLoading}>
          {isLoading ? "Submitting..." : "Submit Feedback"}
        </button>
      </form>
    </div>
  )
}

export default FeedbackForm
